import { App, Button, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crestHttp } from '../../api/client.js';
import { EmptyState } from '../../components/ui/index.js';
import type { RoleCatalogRow } from './access-center-types.js';

export function AccessRoleCatalogPanel() {
  const { t } = useTranslation('accessCenter');
  const { message } = App.useApp();
  const [rows, setRows] = useState<RoleCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState<string>();
  const [q, setQ] = useState('');
  const load = useCallback(() => {
    setLoading(true);
    crestHttp
      .get<{ rows: RoleCatalogRow[] }>('/admin/access/roles', {
        params: { pageSize: 200, domain, q: q || undefined },
      })
      .then(({ data }) => setRows(data.rows ?? []))
      .catch(() => message.error(t('loadFailed')))
      .finally(() => setLoading(false));
  }, [domain, message, q, t]);
  useEffect(() => load(), [load]);
  const reconcile = async () => {
    await crestHttp.post('/admin/access/roles/reconcile');
    message.success(t('roleSyncDone'));
    load();
  };
  return (
    <div className="crest-list-card">
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          prefix={<Search size={14} />}
          placeholder={t('searchRoles')}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder={t('allDomains')}
          value={domain}
          onChange={setDomain}
          style={{ width: 140 }}
          options={['crest', 'crm', 'erp', 'hr', 'scm', 'biz'].map((value) => ({
            value,
            label: value.toUpperCase(),
          }))}
        />
        <Button icon={<RefreshCw size={14} />} onClick={load}>
          {t('common:refresh')}
        </Button>
        <Button onClick={reconcile}>{t('syncCrmRoles')}</Button>
      </Space>
      <Table
        rowKey="role_catalog_id"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: <EmptyState title={t('noRoles')} compact /> }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        columns={[
          {
            title: t('domain'),
            dataIndex: 'domain',
            width: 90,
            render: (value) => <Tag>{String(value).toUpperCase()}</Tag>,
          },
          {
            title: t('roleName'),
            dataIndex: 'name',
            render: (value, row) => (
              <>
                <Typography.Text strong>{value}</Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  {row.description || row.external_role_id}
                </Typography.Text>
              </>
            ),
          },
          { title: t('roleType'), dataIndex: 'role_type', width: 120 },
          {
            title: t('riskLevel'),
            width: 110,
            render: (_, row) =>
              row.metadata?.requiresApproval ? (
                <Tag color="orange">{t('approvalRequired')}</Tag>
              ) : (
                <Tag>{t('ordinaryRole')}</Tag>
              ),
          },
          {
            title: t('workflowEligible'),
            dataIndex: 'workflow_eligible',
            width: 130,
            render: (value) => (value ? t('common:yes') : t('common:no')),
          },
          {
            title: t('common:status'),
            dataIndex: 'status',
            width: 100,
            render: (value) => <Tag color={value === 'active' ? 'green' : 'default'}>{value}</Tag>,
          },
          { title: t('version'), dataIndex: 'version', width: 80, align: 'right' },
        ]}
      />
    </div>
  );
}
